import { ErrorBoundary, Skeleton, HStack, VStack } from '@genial/components';
import { Spacing, Typography } from '@genial/design-tokens';
import { Utils } from '@genial/utils';
import React, { useMemo } from 'react';
import {
  Area,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { useRecoilQuery } from '../../../../../../hooks/useRecoilQuery';
import { assetEvolutionQuery } from '../../../../../../store/home/capture/selector';
import { FundraisingAssetEvolutionPeriodOptions } from '../../../../../../store/home/capture/types';
import { FundraisingAnalysisChartYTick } from '../../../FundraisingAnalysis/components/FundraisingAnalysisChartYTick';
import { FinancialAnalysisComponentTypes } from '../../types';

import { AssetEvolutionTooltip } from './components/AssetEvolutionTooltip';
import { AssetNetValue } from './components/AssetNetValue';
import { ChartLegendOutside } from './components/ChartLegendOutside';
import { PeriodLabel } from './components/PeriodLabel';
import { PeriodSelect } from './components/PeriodSelect';
import { chartNameMap, chartValues, defaultPeriodOption } from './constants';

const axisDefaultConfig = {
  fontFamily: Typography['fontfamily.base.regular'],
  fontSize: Typography['fontsize.sm'],
  tickLine: false,
  axisLine: false,
};

export const AssetEvolution: React.FC<FinancialAnalysisComponentTypes.Props> = ({ isMobile }) => {
  const [periodFilter, setPeriodFilter] =
    React.useState<FundraisingAssetEvolutionPeriodOptions>(defaultPeriodOption);
  const { loading, data, error, refresher } = useRecoilQuery(assetEvolutionQuery({ periodFilter }));

  const chartContent = useMemo(() => {
    if (loading) {
      return <Skeleton width="full" height="260px" borderRadius="sm" />;
    }

    return (
      <ResponsiveContainer width="100%" height={263}>
        <ComposedChart data={data?.chartData ?? []}>
          <defs>
            <linearGradient id="assetNetValueFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3047C72E" stopOpacity={1} />
              <stop offset="95%" stopColor="#FFFFFF" stopOpacity={0} />
            </linearGradient>
          </defs>
          <XAxis
            dataKey={isMobile ? 'referenceDateShortNormalized' : 'referenceDateNormalized'}
            {...axisDefaultConfig}
          />
          <YAxis {...axisDefaultConfig} hide={isMobile} tick={<FundraisingAnalysisChartYTick />} />
          <ReferenceLine
            y={0}
            stroke={Utils.ColorToken.WithOpacity('brand.tertiary.lightest', 0.5)}
          />
          <Tooltip
            content={({ active, label, payload }) => (
              <AssetEvolutionTooltip
                label={label}
                active={active}
                payload={payload}
                nameMap={chartNameMap}
              />
            )}
          />
          <Area
            type="monotone"
            dataKey={chartValues.assetNetValue.dataKey}
            stroke={chartValues.assetNetValue.color}
            fill="url(#assetNetValueFill)"
          />
        </ComposedChart>
      </ResponsiveContainer>
    );
  }, [data?.chartData, isMobile, loading]);

  if (error) {
    return <ErrorBoundary onRefresh={refresher} />;
  }

  return (
    <VStack space={Spacing['stack.xxs']}>
      {isMobile && (
        <PeriodSelect
          periodFilter={periodFilter}
          onSetPeriodFilter={setPeriodFilter}
          loading={loading}
        />
      )}
      {!isMobile && (
        <HStack alignItems="center" justifyContent="space-between">
          <AssetNetValue assetNetValue={data?.currentAssetNetValue} loading={loading} />
          <HStack alignItems="center" space={Spacing['stack.nano']}>
            <PeriodLabel
              startDateNormalized={data?.startDateNormalized}
              endDateNormalized={data?.endDateNormalized}
              loading={loading}
            />
            <PeriodSelect
              periodFilter={periodFilter}
              onSetPeriodFilter={setPeriodFilter}
              loading={loading}
            />
            <ChartLegendOutside loading={loading} />
          </HStack>
        </HStack>
      )}
      {chartContent}
      {isMobile && (
        <>
          <HStack space={Spacing['stack.quarck']} justifyContent="space-between">
            <PeriodLabel
              startDateNormalized={data?.startDateNormalized}
              endDateNormalized={data?.endDateNormalized}
              loading={loading}
            />
            <ChartLegendOutside loading={loading} />
          </HStack>
          <AssetNetValue assetNetValue={data?.currentAssetNetValue} />
        </>
      )}
    </VStack>
  );
};
